/**
 * MCP Service
 *
 * Handles MCP server management API calls:
 * - Generate MCP servers for projects
 * - Get MCP status
 * - Get Claude Desktop configuration
 * - Download configuration files
 */

import { getAPIBaseUrl } from '../environment'

function getApiUrl() {
  return getAPIBaseUrl()
}

/**
 * Enhanced fetch wrapper with better error handling
 */
async function apiFetch(url, options = {}) {
  console.log(`[MCP Service] ${options.method || 'GET'} ${url}`)

  try {
    const response = await fetch(url, options)

    if (!response.ok) {
      // Try to get error details from response body
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`
      try {
        const errorBody = await response.json()
        errorMessage = errorBody.detail || errorBody.message || errorMessage
        console.error(`[MCP Service] API Error:`, errorBody)
      } catch {
        // Response wasn't JSON, use default message
        console.error(`[MCP Service] API Error: ${response.status} ${response.statusText}`)
      }
      throw new Error(errorMessage)
    }

    return response.json()
  } catch (err) {
    // Handle network-level errors (server not running, CORS, etc.)
    if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
      const apiUrl = getApiUrl()
      console.error(`[MCP Service] Network Error: Could not connect to API at ${apiUrl}`)
      console.error(`[MCP Service] Make sure the API server is running`)
      throw new Error(`Could not connect to API server at ${apiUrl}. Is it running?`)
    }

    // Re-throw other errors
    console.error(`[MCP Service] Error:`, err)
    throw err
  }
}

/**
 * Generate MCP server for a project
 * @param {string} scoutAccountId - Account ID
 * @param {string} applicationName - Application name
 * @param {Array} customTools - Optional custom tools
 * @returns {Promise<Object>} Generation result
 */
export async function generateMCP(scoutAccountId, applicationName, customTools = null) {
  const url = `${getApiUrl()}/mcp/generate`

  return apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      scout_account_id: scoutAccountId,
      application_name: applicationName,
      custom_tools: customTools,
    }),
  })
}

/**
 * Get MCP status for a project
 * @param {string} scoutAccountId - Account ID
 * @param {string} applicationName - Application name
 * @returns {Promise<Object>} MCP status
 */
export async function getMCPStatus(scoutAccountId, applicationName) {
  const url = `${getApiUrl()}/mcp/status/${scoutAccountId}/${applicationName}`
  return apiFetch(url)
}

/**
 * Get Claude Desktop configuration for a project
 * @param {string} scoutAccountId - Account ID
 * @param {string} applicationName - Application name
 * @returns {Promise<Object>} Claude Desktop config
 */
export async function getClaudeConfig(scoutAccountId, applicationName) {
  const url = `${getApiUrl()}/mcp/claude-config/${scoutAccountId}/${applicationName}`
  return apiFetch(url)
}

/**
 * Get download URL for Claude config file
 * @param {string} scoutAccountId - Account ID
 * @param {string} applicationName - Application name
 * @returns {string} Download URL
 */
export function getClaudeConfigDownloadUrl(scoutAccountId, applicationName) {
  return `${getApiUrl()}/mcp/download-config/${scoutAccountId}/${applicationName}`
}

/**
 * Get MCP Inspector URL
 * @returns {Promise<Object>} Inspector URL and instructions
 */
export async function getMCPInspectorUrl() {
  const url = `${getApiUrl()}/mcp/inspector-url`
  return apiFetch(url)
}

/**
 * Get project-specific MCP tools
 * @param {string} scoutAccountId - Account ID
 * @param {string} applicationName - Application name
 * @returns {Promise<Object>} Available tools
 */
export async function getProjectTools(scoutAccountId, applicationName) {
  const url = `${getApiUrl()}/mcp/project-tools/${scoutAccountId}/${applicationName}`
  return apiFetch(url)
}

/**
 * Get global MCP tools (from existing MCP server)
 * @returns {Promise<Object>} Available global tools
 */
export async function getGlobalTools() {
  const url = `${getApiUrl()}/mcp/tools`
  return apiFetch(url)
}

/**
 * Get global MCP config
 * @returns {Promise<Object>} Global MCP config
 */
export async function getGlobalConfig() {
  const url = `${getApiUrl()}/mcp/config`
  return apiFetch(url)
}

/**
 * Delete MCP server for a project
 * @param {string} scoutAccountId - Account ID
 * @param {string} applicationName - Application name
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteMCP(scoutAccountId, applicationName) {
  const url = `${getApiUrl()}/mcp/delete/${scoutAccountId}/${applicationName}`
  return apiFetch(url, { method: 'DELETE' })
}

/**
 * Send a chat message to MCP
 * @param {string} scoutAccountId - Account ID
 * @param {string} applicationName - Application name
 * @param {string} message - User message
 * @param {Array} history - Previous chat messages
 * @returns {Promise<Object>} Chat response
 */
export async function sendMCPChat(scoutAccountId, applicationName, message, history = []) {
  const url = `${getApiUrl()}/ai/mcp-chat`

  return apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      scout_account_id: scoutAccountId,
      application_name: applicationName,
      message,
      history,
    }),
  })
}

/**
 * Start MCP server Docker container
 * @param {string} scoutAccountId - Account ID
 * @param {string} applicationName - Application name
 * @param {boolean} rebuild - Force rebuild the image
 * @returns {Promise<Object>} Start result with port and URL
 */
export async function startMCPServer(scoutAccountId, applicationName, rebuild = false) {
  const url = `${getApiUrl()}/mcp/start`

  return apiFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      scout_account_id: scoutAccountId,
      application_name: applicationName,
      rebuild,
    }),
  })
}

/**
 * Stop MCP server Docker container
 * @param {string} scoutAccountId - Account ID
 * @param {string} applicationName - Application name
 * @returns {Promise<Object>} Stop result
 */
export async function stopMCPServer(scoutAccountId, applicationName) {
  const url = `${getApiUrl()}/mcp/stop/${scoutAccountId}/${applicationName}`
  return apiFetch(url, { method: 'POST' })
}

/**
 * Get MCP server container status
 * @param {string} scoutAccountId - Account ID
 * @param {string} applicationName - Application name
 * @returns {Promise<Object>} Container status
 */
export async function getMCPServerStatus(scoutAccountId, applicationName) {
  const url = `${getApiUrl()}/mcp/running/${scoutAccountId}/${applicationName}`
  return apiFetch(url)
}

/**
 * Get MCP server Docker container logs
 * @param {string} scoutAccountId - Account ID
 * @param {string} applicationName - Application name
 * @param {number} tail - Number of log lines to fetch
 * @returns {Promise<Object>} Container logs
 */
export async function getMCPServerLogs(scoutAccountId, applicationName, tail = 100) {
  const url = `${getApiUrl()}/mcp/logs/${scoutAccountId}/${applicationName}?tail=${tail}`
  return apiFetch(url)
}
